import { useRef, useState, useEffect, useMemo, type FC } from 'react'
import { useSelector } from 'react-redux'
import { IApplicationState } from 'Store/types'
import {
  addVehicleVisitingPoint,
  addRecordedChunk,
  clearVehicleVisitingPointsAndVideo,
  removeVehicleVisitingPoint,
  setVideoRecorded,
  saveVisit,
} from '../../Actions/reducer'
import { useTranslation } from 'react-i18next'
import { getVehicleVisitingPoints } from '../../Actions/action'
import { useAppDispatch } from 'Hooks/reduxHooks'
import { base64ToBlob } from 'Utils/files'
import Const from 'Utils/Consts'
import LoadingPage from 'Layouts/LoadingPage/LoadingPage'
import DataNotFoundPage from 'Layouts/DataNotFoundPage/DataNotFoundPage'
import Icon from 'Components/Icon'
import CircularProgressbar from 'Components/CircularProgressbar'
import { useSettings } from 'Contexts/SettingsContext'
import { useGenerateAllAudiosQuery } from 'Services/api'
import { canvasToCompressedFile, fileToBase64 } from 'Utils/compressImage'

const VehicleVisitingPointsMode1Page: FC<{
  handleNext: VoidFunction
  handleBack: VoidFunction
}> = props => {
  const [t] = useTranslation()
  const { settings } = useSettings()
  const width = settings?.REACT_APP_WIDTH ?? Const.env.REACT_APP_WIDTH
  const height = settings?.REACT_APP_HEIGHT ?? Const.env.REACT_APP_HEIGHT
  const quality = settings?.REACT_APP_IMAGE_QUALITY ?? Const.env.REACT_APP_IMAGE_QUALITY
  const videoFormat = settings?.REACT_APP_VIDEO_FORMAT ?? Const.env.REACT_APP_VIDEO_FORMAT
  const frameRate = settings?.REACT_APP_VIDEO_FRAME_RATE ?? Const.env.REACT_APP_VIDEO_FRAME_RATE
  const videoMock = settings?.REACT_APP_VIDEO_MOCK ?? Const.env.REACT_APP_VIDEO_MOCK
  const videoBitsPerSecond =
    settings?.REACT_APP_VIDEO_VIDEO_BITS_PER_SECOND ??
    Const.env.REACT_APP_VIDEO_VIDEO_BITS_PER_SECOND
  const audioBitsPerSecond =
    settings?.REACT_APP_AUDIO_VIDEO_BITS_PER_SECOND ??
    Const.env.REACT_APP_AUDIO_VIDEO_BITS_PER_SECOND

  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment')
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null)
  const [clearForceUpdate, setClearForceUpdate] = useState<number>(0)
  const [videoRecording, setVideoRecording] = useState<boolean>(false)
  const [currentImage, setCurrentImage] = useState<number>(0)
  const [currentImageTime, setCurrentImageTime] = useState<number>(0)
  const [requiredFinishedMessageVisible, setRequiredFinishedMessageVisible] =
    useState<boolean>(false)
  const [captureTimeoutMessageVisible, setCaptureTimeoutMessageVisible] = useState<boolean>(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const videoRecorderRef = useRef<MediaRecorder | null>(null)

  const dispatch = useAppDispatch()
  const { data, videoRecorded } = useSelector(
    (store: IApplicationState) => store.visits.newVisit.vehicleVisitingPoints,
  )
  const { vehicleVisitingPoints } = useSelector((store: IApplicationState) => store.visits)

  const requiredCount = useMemo(() => {
    return vehicleVisitingPoints.data?.filter(x => x.isRequird)?.length ?? 0
  }, [vehicleVisitingPoints.data])

  const visitingPointsTexts = useMemo(
    () =>
      vehicleVisitingPoints.data
        ? vehicleVisitingPoints.data.map(point => point.label as string)
        : [],
    [vehicleVisitingPoints],
  )

  const { data: audios } = useGenerateAllAudiosQuery(
    { inputs: visitingPointsTexts },
    { skip: !visitingPointsTexts },
  )

  useEffect(() => {
    if (vehicleVisitingPoints.data) {
      for (let i = 0; i < vehicleVisitingPoints.data.length; i++) {
        const item = data.find(x => x.id == vehicleVisitingPoints.data[i]?.value)
        if (!item) {
          setCurrentImage(i)
          break
        }
      }
    }
  }, [vehicleVisitingPoints.data])

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (videoRecording && currentImageTime > 0) {
      interval = setInterval(() => {
        setCurrentImageTime(prev => prev - 1)
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [currentImageTime, videoRecording])

  useEffect(() => {
    const point = vehicleVisitingPoints.data[currentImage]
    setCurrentImageTime(point?.imageCaptureTimeSec || 0)
  }, [currentImage, vehicleVisitingPoints.data])

  useEffect(() => {
    const point = vehicleVisitingPoints.data[currentImage]
    const hasTimeLimit = !!point?.imageCaptureTimeSec
    const pointAlreadyCaptured = data.some(x => x.id === point?.value)

    if (videoRecording && hasTimeLimit && currentImageTime === 0 && !pointAlreadyCaptured) {
      handleCaptureTimeout()
    }
  }, [currentImageTime, videoRecording])

  useEffect(() => {
    let stream: MediaStream
    const getCameraStream = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width, height, frameRate, facingMode },
          audio: true,
        })

        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play().catch(() => {})
        }

        setVideoStream(stream)
        const recorder = new MediaRecorder(stream, {
          mimeType: videoFormat,
          videoBitsPerSecond,
          audioBitsPerSecond,
        })

        let totalSize = 0
        const recordedChunks: Blob[] = []

        recorder.ondataavailable = (event: BlobEvent) => {
          if (event.data.size > 0) {
            totalSize += event.data.size

            recordedChunks.push(event.data)

            dispatch(addRecordedChunk(event.data))
          }
        }

        recorder.onstop = async () => {
          console.log(`Original video size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`)
        }
        videoRecorderRef.current = recorder
      } catch (error) {
        console.error('Camera Access Error:', error)
      }
    }

    if (vehicleVisitingPoints.vehicleVisitingPointsLoaded) {
      getCameraStream()
    }

    return () => {
      stream?.getTracks()?.forEach(track => track?.stop())
    }
  }, [facingMode, clearForceUpdate, vehicleVisitingPoints.vehicleVisitingPointsLoaded])

  const handlePlay = (pointLabel: string) => {
    const base64 = audios?.chunks[pointLabel]
    if (!base64) return
    const blob = base64ToBlob(base64)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    if (audioRef.current) {
      audioRef.current.pause()
      URL.revokeObjectURL(audioRef.current.src)
    }
    const audio = new Audio(url)
    audio.playbackRate = 0.85
    audioRef.current = audio
    audio.play().catch(() => {})
  }

  const resetCaptureFlow = () => {
    if (videoRecorderRef.current && videoRecorderRef.current.state !== 'inactive') {
      videoRecorderRef.current.stop()
    }
    dispatch(clearVehicleVisitingPointsAndVideo())
    dispatch(setVideoRecorded(false))
    setVideoRecording(false)
    setRequiredFinishedMessageVisible(false)
    setCaptureTimeoutMessageVisible(false)
    setCurrentImage(0)
    setClearForceUpdate(prev => prev + 1)
  }

  const handleCaptureTimeout = () => {
    resetCaptureFlow()
    setCaptureTimeoutMessageVisible(true)
  }

  const handleCapture = async () => {
    if (!videoRef.current || !canvasRef.current) return

    setCaptureTimeoutMessageVisible(false)

    const context = canvasRef.current.getContext('2d')
    if (!context) return

    canvasRef.current.width = width
    canvasRef.current.height = height
    context.drawImage(videoRef.current, 0, 0, width, height)

    const compressedFile = await canvasToCompressedFile({
      canvas: canvasRef.current,
      fileName: `vehicle-point-${currentImage}.jpg`,
      quality,
    })

    console.log(`Compressed image: ${(compressedFile.size / 1024).toFixed(2)} KB`)

    const imageBase64 = await fileToBase64(compressedFile)

    const pointData = vehicleVisitingPoints.data[currentImage]

    dispatch(
      addVehicleVisitingPoint({
        id: pointData?.value,
        name: pointData?.label,
        isHealthy: true,
        image: imageBase64,
        // image: canvasRef.current.toDataURL('image/png', quality),
        fileType: 'image/png',
        status: 0,
        uploadProgress: 0,
        isPlatePhotoAvailable: pointData?.isPlatePhotoAvailable,
      }),
    )

    const currentDataLength = data.length + 1

    if (!videoRecorded) {
      if (data.length === 0) {
        if (videoRecorderRef.current) {
          videoRecorderRef.current.start()
          setVideoRecording(true)
        } else if (videoMock) {
          setVideoRecording(true)
        }
      }

      if (
        currentDataLength === requiredCount ||
        currentDataLength === vehicleVisitingPoints.data?.length
      ) {
        if (videoRecorderRef.current && videoRecorderRef.current.state !== 'inactive') {
          videoRecorderRef.current.stop()
        }
        setVideoRecording(false)
        dispatch(setVideoRecorded(true))

        if (currentDataLength < vehicleVisitingPoints.data?.length) {
          setRequiredFinishedMessageVisible(true)
        } else {
          videoStream?.getTracks()?.forEach(track => track?.stop())
        }
      }
    }

    if (currentDataLength < vehicleVisitingPoints.data?.length) {
      const nextIndex = currentImage + 1
      handlePlay(vehicleVisitingPoints.data[nextIndex]?.label)
      setCurrentImage(nextIndex)
    }
  }

  const clearVehicleVisitingPointsClickHandler = () => {
    resetCaptureFlow()
  }

  const handleDeleteClick = (id: number, index: number) => {
    dispatch(removeVehicleVisitingPoint(id))
    setCurrentImage(index)
    setClearForceUpdate(prev => prev + 1)
  }

  const handleSetCurrentImageClick = (index: number) => {
    if (!videoRecording && !data.find(x => x.id === vehicleVisitingPoints.data[index]?.value)) {
      setCurrentImage(index)
    }
  }

  return (
    <>
      <div className="flex flex-col grow overflow-auto">
        <div className="px-6 pb-6">
          <div className="text-sm mb-8">
            {t('BY-CAPTURING-AN-IMAGE-OF-EACH-INSPECTION-POINT,-YOU-WILL-MOVE-TO-THE-NEXT-POINT')}
          </div>

          {vehicleVisitingPoints.vehicleVisitingPointsLoading ? (
            <LoadingPage />
          ) : vehicleVisitingPoints.vehicleVisitingPointsLoaded ? (
            data.length < (vehicleVisitingPoints.data?.length || 0) ? (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <div style={{ position: 'relative' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={handleCapture}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '100%',
                      padding: 10,
                      background: 'rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {t('CAPTURE')}
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '100%',
                      padding: 10,
                      background: 'rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {vehicleVisitingPoints.data[currentImage]?.label}
                  </span>
                  <Icon
                    name="Cameraswitch"
                    onClick={() =>
                      setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'))
                    }
                    style={{
                      fontSize: 25,
                      position: 'absolute',
                      top: 7,
                      right: 10,
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  />
                  {currentImageTime > 0 && videoRecording && (
                    <div style={{ position: 'absolute', bottom: 8, left: 10, height: 28 }}>
                      <CircularProgressbar
                        variant="determinate"
                        value={
                          (currentImageTime /
                            (vehicleVisitingPoints.data[currentImage]?.imageCaptureTimeSec || 1)) *
                          100
                        }
                        size={28}
                      />
                      <div className="absolute bottom-0 w-full h-full flex items-center justify-center">
                        <span className="text-center" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {currentImageTime}
                        </span>
                      </div>
                    </div>
                  )}
                  {videoRecording && (
                    <Icon
                      name="Videocam"
                      style={{
                        fontSize: 25,
                        position: 'absolute',
                        top: 7,
                        left: 10,
                        color: 'rgba(255, 0, 0, 0.7)',
                      }}
                    />
                  )}
                </div>
              </div>
            ) : null
          ) : (
            <DataNotFoundPage message="اطلاعات نقاط بازدید یافت نشد." />
          )}

          {vehicleVisitingPoints.vehicleVisitingPointsLoaded && (
            <div className="steps my-6 space-y-2">
              {vehicleVisitingPoints?.data?.map((vehicleVisitingPoint, i) => {
                let item = data.find(x => x.id == vehicleVisitingPoint.value)
                return (
                  <div
                    key={vehicleVisitingPoint.value}
                    className={`group flex items-center gap-2 text-xs font-semibold text-gray-500 ${item ? 'step-done' : i == currentImage ? 'step-current' : ''}`}
                    onClick={() => handleSetCurrentImageClick(i)}
                  >
                    <div className="group-[.step-current]:text-white bg-gray-200 border border-gray-500 group-[.step-done]:bg-primary-lighter group-[.step-current]:bg-primary group-[.step-done]:border-primary-lighter group-[.step-current]:border-primary rounded-full flex items-center justify-center w-4 h-4 group-[.step-current]:w-6 group-[.step-current]:h-6 group-[.step-current]:-mr-1">
                      <div className="group-[.step-done]:hidden text-[9px] group-[.step-current]:text-sm">
                        {i + 1}
                      </div>
                      {item && (
                        <Icon
                          name="checkmark"
                          className="w-[11px] h-[11px] text-primary hidden group-[.step-done]:inline-block"
                        />
                      )}
                    </div>

                    <div className="group-[.step-done]:text-gray-700 group-[.step-current]:text-primary group-[.step-current]:text-sm">
                      {`${vehicleVisitingPoint.label}  `}
                      {vehicleVisitingPoint.isRequird && (
                        <span style={{ color: 'red' }}>{`(${t('REQUIRD')})`}</span>
                      )}
                      {item && vehicleVisitingPoints.data[i]?.isRequird == false && (
                        <span
                          className="link"
                          style={{ color: 'blue' }}
                          onClick={e => {
                            e.stopPropagation()
                            handleDeleteClick(Number(item?.id), i)
                          }}
                        >
                          ({t('REMOVE')})
                        </span>
                      )}
                    </div>

                    {audios && (
                      <span
                        className="text-xs cursor-pointer"
                        onClick={() => handlePlay(vehicleVisitingPoint.label)}
                      >
                        {' '}
                        &#128266;
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {requiredFinishedMessageVisible && (
            <div
              id="alert-info"
              className="flex items-center p-3 text-white rounded-xl bg-gray-800"
              role="alert"
            >
              <div className="bg-info/[.16] text-info p-2 rounded-lg flex justify-center items-center">
                <Icon name="ic_info" className="w-6 h-6" />
              </div>
              <div className="ms-3 text-sm font-semibold grow">
                {t('REQUIRED_FINISHED_MESSAGE')}
              </div>
            </div>
          )}

          {captureTimeoutMessageVisible && (
            <div
              id="alert-timeout"
              className="flex items-center p-3 text-white rounded-xl bg-gray-800"
              role="alert"
            >
              <div className="bg-info/[.16] text-info p-2 rounded-lg flex justify-center items-center">
                <Icon name="ic_info" className="w-6 h-6" />
              </div>
              <div className="ms-3 text-sm font-semibold grow">
                {t('CAPTURE_TIME_EXPIRED_MESSAGE')}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="cta">
        {vehicleVisitingPoints.vehicleVisitingPointsLoaded ? (
          data.length >= requiredCount && (
            <button
              className="btn btn-error btn-lg flex gap-2 items-center justify-center w-full"
              onClick={clearVehicleVisitingPointsClickHandler}
            >
              {t('CLEAR_AUTO_PARTS')}
            </button>
          )
        ) : (
          <button
            className="btn btn-info btn-lg flex gap-2 items-center justify-center w-full"
            onClick={() => dispatch(getVehicleVisitingPoints())}
          >
            {t('GET_VEHICLE_VISITING_POINTS')}
          </button>
        )}
        <div className="flex justify-between items-center gap-2">
          <button
            className="btn btn-warning btn-lg flex gap-2 items-center justify-center w-full"
            onClick={props.handleBack}
            disabled={data.length > 0 && data.length < requiredCount}
          >
            <Icon name="arrow-right" className="w-6 h-6" />
            {t('BACK')}
          </button>
          <button
            className="btn btn-primary btn-lg flex gap-2 items-center justify-center w-full"
            disabled={data.length < requiredCount || !videoRecorded}
            onClick={() => {
              dispatch(saveVisit())
              props.handleNext()
            }}
          >
            {t('NEXT')}
            <Icon name="arrow-left" className="w-6 h-6" />
          </button>
        </div>
      </div>
    </>
  )
}

export default VehicleVisitingPointsMode1Page
